import { updateProfile } from '@firebase/auth'
import * as Sentry from '@sentry/react'
import {
  browserSessionPersistence,
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  setPersistence,
  signInWithPopup,
  type UserCredential,
} from 'firebase/auth'
import { doc, getFirestore, setDoc } from 'firebase/firestore'
import { Logger } from '@/components/Logger'
import { Environment } from '@/configs/firebase'
import { firebaseApp, firebaseAuth } from '@/services/firebaseApp'
import { setApiKey } from '@/utils/apiKeyAuth'

// Sign-in flows await the same persistence setup before creating a tab-scoped session.
const authPersistenceReady = setPersistence(firebaseAuth, browserSessionPersistence)
  .then(() => {
    Logger.log('Firebase Auth: using browser session persistence')
  })
  .catch((error) => {
    Logger.error('Firebase Auth: failed to set browser session persistence', error)
    Sentry.captureMessage('firebaseAuthSetPersistence', {
      extra: {
        error,
      },
    })
  })

if (Environment.environment === 'emulator') {
  Logger.log('Running on firestore emulator', Environment)
  connectAuthEmulator(firebaseAuth, Environment.firebaseAuthEmulatorHost)
}

const db = getFirestore(firebaseApp)

type SignInWithGoogleResult = {
  credentials: UserCredential
  isNewUser: boolean
}

export const ensureSessionPersistence = async () => {
  await authPersistenceReady
}

export const firebaseRegisterUser = async (name: string, email: string, password: string) => {
  try {
    await ensureSessionPersistence()
    // Switching to Firebase auth in this tab must disable API-key mode.
    setApiKey(null)
    const userCredentials = await createUserWithEmailAndPassword(firebaseAuth, email, password)
    if (userCredentials) {
      await updateProfile(userCredentials.user, { displayName: name })
      return { userCredentials, error: null }
    }
  } catch (error: any) {
    Sentry.captureMessage('firebaseRegisterUser', {
      extra: {
        defaultLayout: true,
        location: location.pathname,
        error: error,
      },
    })
    return { userCredentials: null, error }
  }
}

export const firebaseUpdateUser = async (userData: Record<string, unknown>) => {
  try {
    if (firebaseAuth.currentUser) {
      const userRef = doc(db, 'users', firebaseAuth.currentUser.uid)
      await setDoc(userRef, userData, { merge: true })
    }
  } catch (error) {
    Logger.log('firebaseUpdateUser error', error)
    Sentry.captureMessage('firebaseUpdateUser', {
      extra: {
        defaultLayout: true,
        location: location.pathname,
        error: error,
      },
    })
  }
}

export const signInWithGoogle = async (): Promise<SignInWithGoogleResult> => {
  await ensureSessionPersistence()
  // Switching to Firebase auth in this tab must disable API-key mode.
  setApiKey(null)
  const provider = new GoogleAuthProvider()
  const userCredentials = await signInWithPopup(firebaseAuth, provider)
  if (!userCredentials.user) {
    throw new Error(`Couldn't sign in with Google`)
  }
  const additionalInfo = getAdditionalUserInfo(userCredentials)

  return {
    credentials: userCredentials,
    isNewUser: additionalInfo?.isNewUser ?? false,
  }
}
